import { Box, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { selectedToolState } from '~/state/atoms/toolsAtoms';

interface CrosshairsProps {
    vertical: number;
    horizontal: number;
    handleCrosshairChange: (
        type: 'horizontal' | 'vertical',
        delta: number
    ) => void;
}

const Crosshairs = ({
    horizontal,
    vertical,
    handleCrosshairChange,
}: CrosshairsProps) => {
    const theme = useTheme();
    const handleSize = 12;
    const selectedTool = useRecoilValue(selectedToolState);
    const [hoveredLine, setHoveredLine] = useState<
        'horizontal' | 'vertical' | 'center' | null
    >(null);

    const isHorizontalDraggerWithinBoundaries =
        horizontal !== undefined && horizontal >= 0 && horizontal <= 100;
    const isVerticalDraggerWithinBoundaries =
        vertical !== undefined && vertical >= 0 && vertical <= 100;

    const handleMouseDown = (
        event: React.MouseEvent<HTMLDivElement | SVGElement, MouseEvent>,
        type: string
    ) => {
        event.preventDefault();
        const startPos = { x: event.clientX, y: event.clientY };

        const onMouseMove = (moveEvent: MouseEvent) => {
            const { clientX, clientY } = moveEvent;
            let delta: number;

            if (type === 'both') {
                // Handle both axes simultaneously
                const deltaX = clientX - startPos.x;
                const deltaY = clientY - startPos.y;

                startPos.x = clientX;
                startPos.y = clientY;

                handleCrosshairChange('vertical', deltaX);
                handleCrosshairChange('horizontal', deltaY);
                return;
            }

            if (type === 'horizontal') {
                delta = clientY - startPos.y;
                startPos.y = clientY;
            } else {
                delta = clientX - startPos.x;
                startPos.x = clientX;
            }
            handleCrosshairChange(type as 'horizontal' | 'vertical', delta);
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const shouldEnablePointerEvents = selectedTool === 'pan';

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 6,
            }}
        >
            {/* SVG for lines */}
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                }}
            >
                {isHorizontalDraggerWithinBoundaries && (
                    <line
                        x1="0%"
                        y1={`${horizontal}%`}
                        x2="100%"
                        y2={`${horizontal}%`}
                        stroke={
                            hoveredLine === 'horizontal' ||
                            hoveredLine === 'center'
                                ? theme.palette.primary.main
                                : 'gray'
                        }
                        strokeWidth={
                            hoveredLine === 'horizontal' ||
                            hoveredLine === 'center'
                                ? '2'
                                : '1.5'
                        }
                        opacity={0.8}
                        style={{
                            cursor: 'ns-resize',
                            pointerEvents: shouldEnablePointerEvents
                                ? 'auto'
                                : 'none',
                        }}
                        onMouseDown={(event) =>
                            handleMouseDown(event, 'horizontal')
                        }
                        onMouseEnter={() =>
                            shouldEnablePointerEvents &&
                            setHoveredLine('horizontal')
                        }
                        onMouseLeave={() =>
                            shouldEnablePointerEvents && setHoveredLine(null)
                        }
                    />
                )}

                {isVerticalDraggerWithinBoundaries && (
                    <line
                        x1={`${vertical}%`}
                        y1="0%"
                        x2={`${vertical}%`}
                        y2="100%"
                        stroke={
                            hoveredLine === 'vertical' ||
                            hoveredLine === 'center'
                                ? theme.palette.primary.main
                                : 'gray'
                        }
                        strokeWidth={
                            hoveredLine === 'vertical' ||
                            hoveredLine === 'center'
                                ? '2'
                                : '1.5'
                        }
                        opacity={0.8}
                        style={{
                            cursor: 'ew-resize',
                            pointerEvents: shouldEnablePointerEvents
                                ? 'auto'
                                : 'none',
                        }}
                        onMouseDown={(event) =>
                            handleMouseDown(event, 'vertical')
                        }
                        onMouseEnter={() =>
                            shouldEnablePointerEvents &&
                            setHoveredLine('vertical')
                        }
                        onMouseLeave={() =>
                            shouldEnablePointerEvents && setHoveredLine(null)
                        }
                    />
                )}

                {/* Center intersection handle for dragging both axes */}
                {isHorizontalDraggerWithinBoundaries &&
                    isVerticalDraggerWithinBoundaries && (
                        <circle
                            cx={`${vertical}%`}
                            cy={`${horizontal}%`}
                            r={handleSize / 2}
                            fill={theme.palette.primary.main}
                            opacity={0}
                            stroke={theme.palette.primary.dark}
                            strokeWidth="1"
                            style={{
                                cursor: 'move',
                                pointerEvents: shouldEnablePointerEvents
                                    ? 'auto'
                                    : 'none',
                            }}
                            onMouseDown={(event) =>
                                shouldEnablePointerEvents &&
                                handleMouseDown(event, 'both')
                            }
                            onMouseEnter={() =>
                                shouldEnablePointerEvents &&
                                setHoveredLine('center')
                            }
                            onMouseLeave={() =>
                                shouldEnablePointerEvents &&
                                setHoveredLine(null)
                            }
                        />
                    )}
            </svg>

            {/* Handles for horizontal line */}
            {isHorizontalDraggerWithinBoundaries && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: `calc(${horizontal}% - ${handleSize / 2}px)`,
                            width: handleSize,
                            height: handleSize,
                            backgroundColor: theme.palette.primary.main,
                            cursor: 'ns-resize',
                            pointerEvents: 'auto',
                        }}
                        onMouseDown={(event) =>
                            handleMouseDown(event, 'horizontal')
                        }
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: `calc(${horizontal}% - ${handleSize / 2}px)`,
                            width: handleSize,
                            height: handleSize,
                            backgroundColor: theme.palette.primary.main,
                            cursor: 'ns-resize',
                            pointerEvents: 'auto',
                        }}
                        onMouseDown={(event) =>
                            handleMouseDown(event, 'horizontal')
                        }
                    />
                </>
            )}

            {/* Handles for vertical line */}
            {isVerticalDraggerWithinBoundaries && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: `calc(${vertical}% - ${handleSize / 2}px)`,
                            width: handleSize,
                            height: handleSize,
                            backgroundColor: theme.palette.primary.main,
                            cursor: 'ew-resize',
                            pointerEvents: 'auto',
                        }}
                        onMouseDown={(event) =>
                            handleMouseDown(event, 'vertical')
                        }
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: `calc(${vertical}% - ${handleSize / 2}px)`,
                            width: handleSize,
                            height: handleSize,
                            backgroundColor: theme.palette.primary.main,
                            cursor: 'ew-resize',
                            pointerEvents: 'auto',
                        }}
                        onMouseDown={(event) =>
                            handleMouseDown(event, 'vertical')
                        }
                    />
                </>
            )}
        </Box>
    );
};

export default Crosshairs;
